import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Badge,
  Button,
  EmptyState,
  InfoBox,
  LoadingState,
  Toast,
  kstMonthDayStr,
  kstTimeStr,
} from '@biznomi/ui-react'
import { getAnomalies, resolveAnomaly, type AnomalyAlert } from '@/lib/api/admin'

/**
 * 이상 탐지 — 부정 태깅 패턴과 체인 실패를 모아 보는 곳.
 *
 * ⚠️ 이건 알림을 보내지 않는다. 화면 안에서만 보인다 (§16-D14).
 *   룰이 자동으로 푸시를 쏘는 건 §원칙 2 위반이고, 오탐이 직원에게 바로 가면
 *   그게 더 큰 문제가 된다. 관리자가 들어와서 보고 판단한다.
 *
 * 그리고 ADMIN 전용이다 (§16-D15) — 부서장 권한 남용·노사 분쟁 방지.
 */

/** 서버 enum 을 사람 말로. 새 유형이 생기면 코드 그대로 보이므로 하드코딩해도 안전하다 */
const TYPE_LABEL: Record<string, { label: string; desc: string; tone: 'warning' | 'destructive' }> = {
  RAPID_TAG: {
    label: '연속 태깅',
    desc: '1분 안에 3번 넘게 찍혔습니다. 남이 대신 찍었거나 오작동일 수 있습니다',
    tone: 'warning',
  },
  CHAIN_FAILED: {
    label: '체인 계산 실패',
    desc: '태깅은 저장됐지만 위변조 검증 체인에 넣지 못했습니다',
    tone: 'destructive',
  },
}

function typeInfo(type: string) {
  return TYPE_LABEL[type] ?? { label: type, desc: '', tone: 'warning' as const }
}

export default function Anomalies() {
  const qc = useQueryClient()
  const [showResolved, setShowResolved] = useState(false)
  const [toast, setToast] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'anomalies', showResolved],
    queryFn: async () => {
      const res = await getAnomalies({ resolved: showResolved, take: 100 })
      if (!res.success || !res.data) throw new Error(res.error ?? '이상 탐지 내역을 불러오지 못했습니다')
      return res.data
    },
  })

  const resolve = useMutation({
    mutationFn: (id: string) => resolveAnomaly(id),
    onSuccess: (res) => {
      setToast(res.success ? '처리했습니다' : (res.error ?? '실패했습니다'))
      qc.invalidateQueries({ queryKey: ['admin', 'anomalies'] })
    },
  })

  if (isLoading) return <LoadingState />
  if (error) return <InfoBox tone="danger">{(error as Error).message}</InfoBox>

  const items = data?.items ?? []
  const counts = data?.typeCounts ?? {}

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-bold">이상 탐지</h1>
        <p className="mt-1 text-sm text-slate-500">
          알림은 보내지 않습니다. 여기서 보고 판단하세요.
        </p>
      </header>

      {/* 유형별 집계 — 하드코딩하지 않고 서버가 준 것만 그린다 */}
      {Object.keys(counts).length > 0 && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {Object.entries(counts).map(([type, n]) => (
            <div key={type} className="rounded-card bg-card p-4">
              <p className="text-sm text-slate-500">{typeInfo(type).label}</p>
              <p className="mt-1 text-2xl font-bold tabular-nums">{n}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Button size="sm" variant={!showResolved ? 'default' : 'secondary'} onClick={() => setShowResolved(false)}>
          미처리
        </Button>
        <Button size="sm" variant={showResolved ? 'default' : 'secondary'} onClick={() => setShowResolved(true)}>
          처리됨
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="rounded-card bg-card">
          <EmptyState
            title={showResolved ? '처리한 내역이 없습니다.' : '이상 징후가 없습니다.'}
            hint={showResolved ? undefined : '연속 태깅이나 체인 실패가 생기면 여기 쌓입니다.'}
            icon={showResolved ? 'inbox' : 'alert'}
          />
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((a: AnomalyAlert) => {
            const info = typeInfo(a.type)
            return (
              <li key={a.id} className="rounded-card bg-card p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant={info.tone}>{info.label}</Badge>
                      {a.employee && (
                        <span className="text-sm font-medium">
                          {a.employee.name}
                          {a.employee.employeeNumber && (
                            <span className="ml-1 text-xs text-slate-400">{a.employee.employeeNumber}</span>
                          )}
                        </span>
                      )}
                      <span className="text-xs text-slate-400">
                        {kstMonthDayStr(a.createdAt)} {kstTimeStr(a.createdAt)}
                      </span>
                    </div>

                    {/* 서버가 사람이 읽는 문장을 준다. 없으면 유형 설명으로 대신한다 */}
                    <p className="text-sm text-slate-600">{a.message || info.desc}</p>
                  </div>

                  {!a.resolvedAt && (
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={resolve.isPending && resolve.variables === a.id}
                      onClick={() => resolve.mutate(a.id)}
                    >
                      확인함
                    </Button>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      )}

      <p className="text-xs leading-relaxed text-slate-400">
        야간·새벽 태깅은 이상으로 보지 않습니다. 교대 근무가 있는 곳에서는 정상이기 때문입니다.
      </p>

      {toast && <Toast message={toast} variant="success" onClose={() => setToast('')} />}
    </div>
  )
}
